// Chatbot script — Capi conversational agent (mixed/inclusive version)
// Updated: 06.07.26 flow
//
// Step types:
//   'continue'     single centred button, different visual from pill options
//   'question'     standard multi-option pill buttons
//   'multi-select' pick exactly `count` options before advancing
//   'free-text'    textarea + submit button
//
// Dynamic tokens:
//   {{emotion}}    replaced at render time with the last saved emotion label
//
// Special next values:
//   '[return to feed]'  emits chatbot-complete and closes the overlay

export const STEPS = [

  // ─── Intro ────────────────────────────────────────────────────────────────
  // q0 botMessage is shown in the intro popup screen (not as a chat bubble).
  // q1 is the first interactive step inside the chat UI.

  {
    id: 'q0',
    type: 'continue',
    botMessage: 'Bonjour !\nJe m\'appelle Capi. Je suis ton compagnon de pause. Ravi de faire ta connaissance !',
    options: [{ label: 'Clique pour continuer ➡️', next: 'q1' }],
  },
  {
    id: 'q1',
    type: 'question',
    botMessage: 'Pour me répondre, clique sur un des boutons ci-dessous :',
    options: [
      { label: 'Bonjour Capi !',  next: 'q2' },
      { label: 'D\'accord 👌',    next: 'q2' },
      { label: 'C\'est noté ✅',  next: 'q2' },
    ],
  },

  // ─── Feelings check-in ────────────────────────────────────────────────────

  {
    id: 'q2',
    type: 'question',
    botMessage: 'Tu viens de voir beaucoup de publications sur la transformation physique (fitness, "glow-up", avant/après).\nComment tu te sens, là, maintenant ?',
    options: [
      { label: 'Très bien 😊',          next: 'q3' },
      { label: 'Plutôt bien 🙂',        next: 'q3' },
      { label: 'Bof, ça peut aller 😕', next: 'q6' },
      { label: 'Pas très bien 😞',      next: 'q6' },
      { label: 'Je sais pas trop 🤔',   next: 'q6' },
    ],
  },

  // ─── Positive path ────────────────────────────────────────────────────────

  {
    id: 'q3',
    type: 'question',
    botMessage: 'Et si tu devais choisir une seule émotion pour décrire comment tu te sens là, tout de suite ?',
    options: [
      { label: 'Inspiré(e) / motivé(e) 🌟', next: 'q4', saveEmotion: true },
      { label: 'Joyeux(se) 😊',             next: 'q4', saveEmotion: true },
      { label: 'Amusé(e) 😄',               next: 'q4', saveEmotion: true },
      { label: 'Détendu(e) 😌',             next: 'q4', saveEmotion: true },
    ],
  },
  {
    id: 'q4',
    type: 'question',
    botMessage: 'Tu m\'as dit que tu te sens {{emotion}}.\nEst-ce que ça arrive souvent quand tu scrolles ce type de publications ?',
    options: [
      { label: 'Oui, souvent',          next: 'q5' },
      { label: 'Parfois',               next: 'q5' },
      { label: 'Non c\'est inhabituel', next: 'q5' },
    ],
  },
  {
    id: 'q5',
    type: 'continue',
    botMessage: 'Top que tu te sentes bien aujourd\'hui ! Ces publications n\'ont pas toujours cet effet.',
    options: [{ label: 'Clique pour continuer ➡️', next: 'q9' }],
  },

  // ─── Negative / neutral path ──────────────────────────────────────────────

  {
    id: 'q6',
    type: 'question',
    botMessage: 'Et si tu devais choisir une seule émotion pour décrire comment tu te sens là, tout de suite ?',
    options: [
      { label: 'Complexé(e) 😳',              next: 'q7', saveEmotion: true },
      { label: 'Frustré(e) 😣',               next: 'q7', saveEmotion: true },
      { label: 'En colère 😤',                next: 'q7', saveEmotion: true },
      { label: 'Triste 😢',                   next: 'q7', saveEmotion: true },
      { label: 'Indifférent(e) 🫥',           next: 'q7', saveEmotion: true },
      { label: 'Vide 😵',                     next: 'q7', saveEmotion: true },
      { label: 'Blasé(e) 😑',                 next: 'q7', saveEmotion: true },
    ],
  },
  {
    id: 'q7',
    type: 'question',
    botMessage: 'Tu m\'as dit que tu te sens {{emotion}}.\nEst-ce que ça arrive souvent quand tu scrolles ce type de publications ?',
    options: [
      { label: 'Oui, souvent',          next: 'q8' },
      { label: 'Parfois',               next: 'q8' },
      { label: 'Non c\'est inhabituel', next: 'q8' },
    ],
  },
  {
    id: 'q8',
    type: 'continue',
    botMessage: 'Je comprends, tu n\'es pas seul(e) à te sentir comme ça.',
    options: [{ label: 'Clique pour continuer ➡️', next: 'q9' }],
  },

  // ─── Awareness ────────────────────────────────────────────────────────────

  {
    id: 'q9',
    type: 'question',
    botMessage: 'Ces publications peuvent affecter l\'image que tu as de ton corps, sans même que tu t\'en rendes compte.\n\nPrès d\'1 jeune sur 2 dit que ces publications lui donnent envie de changer son apparence. Et ça peut créer de vraies insécurités.',
    options: [
      { label: 'Wow, je savais pas 😮',                           next: 'q10' },
      { label: 'Je me reconnais là-dedans 😞',                    next: 'q10' },
      { label: 'Je suis pas sûr(e) de me sentir concerné(e) 🤔',  next: 'q10' },
    ],
  },

  // ─── Exercise proposal ────────────────────────────────────────────────────

  {
    id: 'q10',
    type: 'question',
    botMessage: 'Prendre un peu de recul après avoir vu ce type de publications, c\'est toujours utile.\nJe te propose qu\'on essaie une petite activité ensemble ?',
    options: [
      { label: 'Allez, on essaye ! ✨',                     next: 'exq0' },
      { label: 'Pourquoi pas... 🤔',                        next: 'exq0' },
      { label: 'Curieux(se) de voir ce que tu proposes 👀', next: 'exq0' },
    ],
  },

  // ─── Exercise flow ────────────────────────────────────────────────────────

  {
    id: 'exq0',
    type: 'question',
    botMessage: 'On passe beaucoup de temps à penser à quoi notre corps ressemble. Et si on pensait plutôt à ce qu\'il peut faire ? 🌱',
    options: [
      { label: 'Bonne idée 💡',         next: 'exq1' },
      { label: 'Allons-y 🙌',           next: 'exq1' },
      { label: 'Je suis partant(e) ✅',  next: 'exq1' },
    ],
  },
  {
    id: 'exq1',
    type: 'question',
    botMessage: 'Prends un moment pour réfléchir à tout ce que ton corps te permet de faire (ex. marcher, rire, serrer quelqu\'un dans tes bras...)',
    options: [
      { label: 'Facile, plein de choses !',               next: 'exq2' },
      { label: 'Quelques trucs me viennent en tête',      next: 'exq2' },
      { label: 'C\'est un peu flou, je ne sais pas trop', next: 'exq3' },
      { label: 'Je n\'y avais jamais réfléchi',            next: 'exq3' },
    ],
  },
  {
    id: 'exq2',
    type: 'continue',
    botMessage: 'Cool ! Voilà d\'autres choses que ton corps fait, auxquelles on pense moins souvent…',
    options: [{ label: 'Clique pour continuer ➡️', next: 'exq4' }],
  },
  {
    id: 'exq3',
    type: 'continue',
    botMessage: 'C\'est pas toujours facile d\'y penser ! Laisse-moi t\'aider à réfléchir à quelques exemples…',
    options: [{ label: 'Clique pour continuer ➡️', next: 'exq4' }],
  },
  {
    id: 'exq4',
    type: 'multi-select',
    count: 3,
    botMessage: 'Quelles fonctions de ton corps comptent le plus pour toi ? Sélectionnes-en 3.',
    options: [
      { label: 'Voir, sentir, ressentir des émotions' },
      { label: 'Bouger / marcher / se balader' },
      { label: 'Danser / chanter / écouter ou faire de la musique' },
      { label: 'Réfléchir / penser / rêver' },
      { label: 'Goûter de nouveaux plats / cuisiner' },
      { label: 'Communiquer / embrasser / serrer dans ses bras' },
      { label: 'Dessiner / peindre / sculpter' },
      { label: 'Lire / écrire' },
      { label: 'Respirer / cicatriser / guérir d\'une maladie' },
    ],
    next: 'exq5',
  },
  {
    id: 'exq5',
    type: 'free-text',
    botMessage: 'Cool ! Pourquoi ces fonctions plus que les autres ? J\'aimerais bien savoir en quelques mots.',
    placeholder: 'Parce que...',
    next: 'exq6',
  },
  {
    id: 'exq6',
    type: 'question',
    botMessage: 'Merci de m\'avoir partagé ça. C\'est super de savoir ce qui compte pour toi !\n\nTu viens de réfléchir à tout un tas de choses que ton corps te permet de faire.\n\nAprès avoir scrollé, on a parfois tendance à oublier que son corps est bien plus que ce qu\'on voit. Il fait des choses incroyables tous les jours !',
    options: [
      { label: 'C\'est vrai, maintenant que tu le dis... 🧐', next: 'q12' },
      { label: 'D\'accord 👌',                                next: 'q12' },
      { label: 'J\'y penserai à l\'avenir 💭',                next: 'q12' },
    ],
  },

  // ─── Farewell ─────────────────────────────────────────────────────────────

  {
    id: 'q12',
    type: 'question',
    botMessage: 'Et voilà ! En retournant sur ton feed, n\'oublie pas tout ce que ton corps peut faire. Bon scroll !',
    options: [
      { label: 'Au revoir Capi 👋',      next: '[return to feed]' },
      { label: 'Je retourne scroller 📲', next: '[return to feed]' },
    ],
  },
];
